import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Slider } from '../components/ui/slider';
import { Input } from '../components/ui/input';
import { ChevronDown, ChevronUp, TrendingUp, Wallet, Percent, Clock } from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, CartesianGrid
} from 'recharts';

const fmt = (v) => new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(v);
const fmtAxis = (v) => v >= 1e6 ? `${(v / 1e6).toFixed(1)}M` : v >= 1e3 ? `${(v / 1e3).toFixed(0)}k` : v;

const SliderInput = ({ label, icon, value, setValue, min, max, step, suffix }) => {
  const [localValue, setLocalValue] = useState(String(value));
  const [editing, setEditing] = useState(false);
  const timerRef = useRef(null);

  // Sync local value when parent value changes (from slider) and not editing
  useEffect(() => {
    if (!editing) {
      setLocalValue(String(value));
    }
  }, [value, editing]);

  const commit = (raw) => {
    const n = Number(raw);
    if (!isNaN(n)) {
      const clamped = Math.min(max, Math.max(min, n));
      setValue(clamped);
      setLocalValue(String(clamped));
    } else {
      setLocalValue(String(value));
    }
  };

  const handleChange = (e) => {
    const raw = e.target.value;
    setLocalValue(raw);
    // Debounce 500ms
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => commit(raw), 500);
  };

  const handleBlur = () => {
    clearTimeout(timerRef.current);
    setEditing(false);
    commit(localValue);
  };

  const handleFocus = () => setEditing(true);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-cap-text-secondary text-sm flex items-center gap-2">
          {icon} {label}
        </label>
        <div className="flex items-center gap-1">
          <Input
            type="number"
            value={localValue}
            onChange={handleChange}
            onBlur={handleBlur}
            onFocus={handleFocus}
            className="w-24 h-8 text-right bg-cap-bg border-white/10 text-white text-sm font-mono"
            data-testid={`input-${label.toLowerCase().replace(/[^a-z]/g, '-')}`}
          />
          {suffix && <span className="text-cap-text-muted text-sm">{suffix}</span>}
        </div>
      </div>
      <Slider
        value={[value]}
        onValueChange={([v]) => setValue(v)}
        min={min} max={max} step={step}
        className="w-full"
        data-testid={`slider-${label.toLowerCase().replace(/[^a-z]/g, '-')}`}
      />
    </div>
  );
};

const OutilsPage = () => {
  const [capital, setCapital] = useState(10000);
  const [monthly, setMonthly] = useState(200);
  const [rate, setRate] = useState(8);
  const [years, setYears] = useState(20);
  const [freqMonthly, setFreqMonthly] = useState(true);
  const [showTable, setShowTable] = useState(false);

  const { chartData, finalValue, totalInvested, interestEarned, multiplier } = useMemo(() => {
    const r = rate / 100;
    const data = [];
    let cumTotal = capital;
    let cumInvested = capital;

    data.push({ year: 0, invested: capital, interest: 0, total: capital });

    for (let y = 1; y <= years; y++) {
      // Intérêts sur le capital existant
      cumTotal = cumTotal + cumTotal * r;
      // Apports de l'année
      cumTotal = cumTotal + monthly * 12;
      cumInvested += monthly * 12;

      const interest = cumTotal - cumInvested;
      data.push({
        year: y,
        invested: Math.round(cumInvested),
        interest: Math.round(interest > 0 ? interest : 0),
        total: Math.round(cumTotal),
      });
    }

    const last = data[data.length - 1];
    return {
      chartData: data,
      finalValue: last.total,
      totalInvested: last.invested,
      interestEarned: last.interest,
      multiplier: last.invested > 0 ? (last.total / last.invested) : 0,
    };
  }, [capital, monthly, rate, years, freqMonthly]);

  const rule72 = rate > 0 ? (72 / rate) : Infinity;

  const ResultCard = ({ label, value, accent }) => (
    <div className={`rounded-xl border p-4 ${accent ? 'bg-cap-emerald/10 border-cap-emerald/20' : 'bg-cap-bg-elevated border-white/5'}`}>
      <p className="text-cap-text-muted text-xs mb-1">{label}</p>
      <p className={`font-display text-xl font-bold ${accent ? 'text-cap-emerald' : 'text-white'}`}>{value}</p>
    </div>
  );

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    return (
      <div className="rounded-lg px-4 py-3 shadow-xl" style={{ background: '#1a1d27', border: '1px solid #00e5a0', borderRadius: '0.5rem' }}>
        <p className="text-white font-bold text-sm mb-2">Année {d.year}</p>
        <p className="text-cap-text-secondary text-xs">Capital versé : <span className="text-white font-mono">{fmt(d.invested)} €</span></p>
        <p className="text-cap-text-secondary text-xs">Intérêts : <span className="font-mono" style={{ color: '#00e5a0' }}>{fmt(d.interest)} €</span></p>
        <p className="text-cap-text-secondary text-xs">Total : <span className="text-white font-mono font-bold">{fmt(d.total)} €</span></p>
      </div>
    );
  };

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto space-y-10" data-testid="outils-page">
      {/* ZONE 1 — Header */}
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-white mb-2">
          Intérêts Composés
        </h1>
        <p className="text-cap-text-secondary text-base max-w-2xl">
          Découvrez la puissance des intérêts composés — le principe fondamental de tout investissement long terme
        </p>
      </div>

      {/* ZONE 2 — Calculator */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
        {/* Left column — Inputs (40%) */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-cap-bg-elevated border border-white/5 rounded-2xl p-6 space-y-5">
            <SliderInput label="Capital initial" icon={<Wallet className="w-4 h-4" />} value={capital} setValue={setCapital} min={0} max={100000} step={500} suffix="€" />
            <SliderInput label="Apport mensuel" icon={<TrendingUp className="w-4 h-4" />} value={monthly} setValue={setMonthly} min={0} max={5000} step={50} suffix="€" />
            <SliderInput label="Taux annuel" icon={<Percent className="w-4 h-4" />} value={rate} setValue={setRate} min={0} max={20} step={0.1} suffix="%" />
            <SliderInput label="Durée" icon={<Clock className="w-4 h-4" />} value={years} setValue={setYears} min={1} max={40} step={1} suffix="ans" />

            {/* Frequency toggle */}
            <div className="flex items-center justify-between">
              <span className="text-cap-text-secondary text-sm">Capitalisation</span>
              <div className="flex rounded-lg border border-white/10 overflow-hidden">
                <button
                  onClick={() => setFreqMonthly(true)}
                  className={`px-3 py-1.5 text-xs font-medium transition-colors ${freqMonthly ? 'bg-cap-emerald/20 text-cap-emerald' : 'text-cap-text-muted hover:text-white'}`}
                  data-testid="freq-monthly"
                >Mensuelle</button>
                <button
                  onClick={() => setFreqMonthly(false)}
                  className={`px-3 py-1.5 text-xs font-medium transition-colors ${!freqMonthly ? 'bg-cap-emerald/20 text-cap-emerald' : 'text-cap-text-muted hover:text-white'}`}
                  data-testid="freq-annual"
                >Annuelle</button>
              </div>
            </div>
          </div>

          {/* Result cards */}
          <div className="grid grid-cols-2 gap-3">
            <ResultCard label="Capital final" value={`${fmt(finalValue)} €`} accent />
            <ResultCard label="Total investi" value={`${fmt(totalInvested)} €`} />
            <ResultCard label="Intérêts générés" value={`${fmt(interestEarned)} €`} />
            <ResultCard label="Multiplicateur" value={`x${multiplier.toFixed(1)}`} />
          </div>
        </div>

        {/* Right column — Chart (60%) */}
        <div className="lg:col-span-3 space-y-4">
          <div className="bg-cap-bg-elevated border border-white/5 rounded-2xl p-6">
            <div className="h-[380px]">
              <ResponsiveContainer>
                <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gradInterest" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#00e5a0" stopOpacity={0.7} />
                      <stop offset="100%" stopColor="#00e5a0" stopOpacity={0.7} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid horizontal={true} vertical={false} stroke="rgba(255,255,255,0.08)" />
                  <XAxis
                    dataKey="year"
                    tick={{ fill: '#94a3b8', fontSize: 12 }}
                    tickFormatter={(v) => `${v}a`}
                    ticks={[...new Set([0, ...Array.from({ length: Math.floor(years / 5) }, (_, i) => (i + 1) * 5).filter(v => v <= years), years])]}
                  />
                  <YAxis tick={{ fill: '#94a3b8', fontSize: 13 }} tickFormatter={fmtAxis} width={55} />
                  <Tooltip content={<CustomTooltip />} />
                  <ReferenceLine y={finalValue} stroke="#00e5a0" strokeDasharray="6 4" strokeOpacity={0.4} />
                  <Area type="monotone" dataKey="invested" stackId="1" fill="#2a3550" stroke="#3d4f6f" strokeWidth={1.5} name="Capital versé" />
                  <Area type="monotone" dataKey="interest" stackId="1" fill="url(#gradInterest)" stroke="#00e5a0" strokeWidth={2} name="Intérêts" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            {/* Legend */}
            <div className="flex items-center justify-center gap-6 mt-4" data-testid="chart-legend">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full" style={{ background: '#2a3550' }} />
                <span className="text-cap-text-secondary text-xs">Capital versé</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full" style={{ background: '#00e5a0' }} />
                <span className="text-cap-text-secondary text-xs">Intérêts générés</span>
              </div>
            </div>
          </div>

          {/* Dynamic summary */}
          <div className="bg-cap-bg-elevated/50 border border-white/5 rounded-xl px-5 py-4">
            <p className="text-cap-text-secondary text-sm leading-relaxed">
              Avec un capital initial de <span className="text-white font-semibold">{fmt(capital)} €</span> et en investissant{' '}
              <span className="text-white font-semibold">{fmt(monthly)} €/mois</span> pendant{' '}
              <span className="text-white font-semibold">{years} ans</span> à{' '}
              <span className="text-white font-semibold">{rate}%</span>, votre capital atteint{' '}
              <span className="text-cap-emerald font-bold">{fmt(finalValue)} €</span> dont{' '}
              <span className="text-cap-emerald font-semibold">{fmt(interestEarned)} €</span> d'intérêts générés — soit{' '}
              <span className="text-white font-bold">x{multiplier.toFixed(1)}</span> votre mise.
            </p>
          </div>
        </div>
      </div>

      {/* ZONE 3 — Progression Table */}
      <div className="bg-cap-bg-elevated border border-white/5 rounded-2xl overflow-hidden">
        <button
          onClick={() => setShowTable(!showTable)}
          className="w-full flex items-center justify-between px-6 py-4 hover:bg-white/[0.02] transition-colors"
          data-testid="toggle-table"
        >
          <span className="text-white font-display font-bold text-sm">Voir la progression année par année</span>
          {showTable ? <ChevronUp className="w-5 h-5 text-cap-text-muted" /> : <ChevronDown className="w-5 h-5 text-cap-text-muted" />}
        </button>
        {showTable && (
          <div className="overflow-x-auto border-t border-white/5">
            <table className="w-full text-left" data-testid="progression-table">
              <thead>
                <tr className="border-b border-white/5 text-cap-text-muted text-xs uppercase">
                  <th className="py-3 px-4">Année</th>
                  <th className="py-3 px-4 text-right">Capital versé</th>
                  <th className="py-3 px-4 text-right">Intérêts cumulés</th>
                  <th className="py-3 px-4 text-right">Total</th>
                  <th className="py-3 px-4 text-right">Croissance</th>
                </tr>
              </thead>
              <tbody>
                {chartData.filter(d => d.year > 0).map((d, i) => {
                  const prev = chartData[i]; // previous year entry (chartData[0] is year 0)
                  const growth = prev && prev.total > 0 ? ((d.total - prev.total) / prev.total * 100) : 0;
                  const isLast = d.year === years;
                  return (
                    <tr key={d.year} className={`border-b border-white/5 ${isLast ? 'bg-cap-emerald/5' : i % 2 === 0 ? 'bg-white/[0.01]' : ''}`}>
                      <td className={`py-2.5 px-4 font-mono text-sm ${isLast ? 'text-cap-emerald font-bold' : 'text-white'}`}>{d.year}</td>
                      <td className="py-2.5 px-4 text-right text-cap-text-secondary font-mono text-sm">{fmt(d.invested)} €</td>
                      <td className="py-2.5 px-4 text-right text-cap-emerald font-mono text-sm">{fmt(d.interest)} €</td>
                      <td className={`py-2.5 px-4 text-right font-mono text-sm font-bold ${isLast ? 'text-cap-emerald' : 'text-white'}`}>{fmt(d.total)} €</td>
                      <td className="py-2.5 px-4 text-right text-cap-text-muted font-mono text-sm">+{growth.toFixed(1)}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ZONE 4 — Educational */}
      <div className="space-y-6">
        <h2 className="font-display text-xl font-bold text-white">Comment fonctionnent les intérêts composés ?</h2>

        {/* Formula */}
        <div className="bg-cap-bg-elevated border border-white/5 rounded-2xl p-6">
          <h3 className="text-white font-display font-bold text-sm mb-4">La formule</h3>
          <div className="bg-cap-bg rounded-xl border border-cap-emerald/20 px-6 py-4 mb-4 font-mono text-center">
            <span className="text-cap-emerald text-lg">Vf = Vi</span>
            <span className="text-white text-lg"> x </span>
            <span className="text-cap-emerald text-lg">(1 + r/n)</span>
            <span className="text-white align-super text-sm">(n x t)</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
            {[
              ['Vf', 'Valeur finale'],
              ['Vi', 'Capital initial'],
              ['r', `Taux annuel (ex : 0.${String(rate * 10).padStart(2, '0')} pour ${rate}%)`],
              ['n', 'Fréquence de capitalisation/an'],
              ['t', 'Durée en années'],
            ].map(([sym, desc]) => (
              <div key={sym} className="flex items-start gap-2">
                <span className="text-cap-emerald font-mono font-bold">{sym}</span>
                <span className="text-cap-text-muted">{desc}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Snowball effect */}
        <div className="bg-cap-bg-elevated border border-white/5 rounded-2xl p-6">
          <h3 className="text-white font-display font-bold text-sm mb-3">L'effet boule de neige</h3>
          <p className="text-cap-text-secondary text-sm leading-relaxed">
            Contrairement aux intérêts simples calculés uniquement sur le capital initial,
            les intérêts composés se calculent sur le capital accumulé — intérêts compris.
            Chaque année, vos intérêts génèrent eux-mêmes des intérêts, créant un effet
            boule de neige exponentiel.
          </p>
        </div>

        {/* Rule of 72 */}
        <div className="bg-cap-bg-elevated border border-cap-emerald/10 rounded-2xl p-6">
          <h3 className="text-white font-display font-bold text-sm mb-3">La règle des 72</h3>
          <p className="text-cap-text-secondary text-sm leading-relaxed mb-4">
            Divisez 72 par votre taux annuel pour estimer le nombre d'années nécessaires pour doubler votre capital.
          </p>
          <div className="bg-cap-bg rounded-xl border border-white/10 px-5 py-3 flex items-center gap-4">
            <div className="font-mono text-cap-text-secondary text-sm">
              72 / <span className="text-cap-emerald font-bold">{rate}%</span> ={' '}
              <span className="text-white font-bold text-lg">{rate > 0 ? rule72.toFixed(1) : '∞'} ans</span>
            </div>
            <span className="text-cap-text-muted text-xs">pour doubler votre capital</span>
          </div>
        </div>
      </div>

      {/* ZONE 5 — Disclaimer */}
      <div className="border-t border-white/5 pt-6 pb-8">
        <p className="text-cap-text-muted text-xs text-center leading-relaxed max-w-2xl mx-auto">
          Cet outil est fourni à titre indicatif uniquement. Les rendements passés ne préjugent pas des rendements futurs.
          CAP OS ne fournit pas de conseil en investissement.
        </p>
      </div>
    </div>
  );
};

export default OutilsPage;
